/**
 *
 * @module helpers/ModelHelpers
 * @author shojib [mahmud.hossain@mlb.com]
 * @description module for adding scores model helper functions
 *
 */

import bamHydrates from 'bam-hydrates/index';

/**
 *
 * @name ModelHelpers
 * @description helper functions for Model
 *
 */
const ModelHelpers = {

    /**
     *
     * @method constructPath for PostSeason mode
     * @param {Object} opts
     * @returns {String}
     * @description path construction based on options
     *
     */
    constructPostSeasonPath(opts = {}) {

        const { hydratesBase, date, hydratesResponsiveAndMIG, hydratesSeriesStatus, hydratesButtons, useHttps, lang } = opts;
        const scheduleHydrateObj = bamHydrates.merge(hydratesBase, hydratesResponsiveAndMIG, hydratesSeriesStatus, hydratesButtons);
        const scheduleHydrateStr = bamHydrates.stringify(scheduleHydrateObj);

        let season = date.length >= 4 ? date.split('-')[0] : '';
        season = season.length === 4 ? season : new Date().getUTCFullYear();

        let { path } = opts;
        path = !useHttps ? path : path.replace('http:', 'https:');

        return `
            ${path}/api/v1/schedule/postseason/series
            ?sportId=1
            &season=${season}
            &hydrate=${scheduleHydrateStr}
            &useFeaturedGame=true
            &useLatestGames=true
            &language=${lang}
            `.replace(/\s/g, '');

    },

    /**
     *
     * @method constructPath for MIG mode
     * @param {Object} opts
     * @returns {String}
     * @description path construction based on options
     *
     */
    constructMigPath(opts = {}) {

        const { hydratesBase, hydratesResponsiveAndMIG, hydratesButtons, hydratesSeriesStatus } = opts;
        const { date, useHttps, teamId, gameTypes, lang, season, hydratesPromotions, hydratesCurrentTeam } = opts;

        let scheduleHydrateObj = bamHydrates.merge(hydratesBase, hydratesResponsiveAndMIG, hydratesButtons, hydratesSeriesStatus);

        if (opts.sport === 'milb') {
            hydratesCurrentTeam.person['appContext=minorLeague'] = {};
            scheduleHydrateObj = bamHydrates.merge(scheduleHydrateObj, hydratesPromotions, hydratesCurrentTeam);
        }

        let { gamesPrev, gamesNext } = opts;

        const showPrev = gamesPrev > 0;
        const showNext = gamesNext > 0;

        const scheduleHydrateStr = bamHydrates.stringify(scheduleHydrateObj);
        const seasonStr = (season && season.trim() !== '') ? `season=${season},` : '';

        const prevScheduleHydrate = `
            date=${date},
            ${seasonStr}
            limit=${gamesPrev},
            gameType=[${gameTypes}],
            ${scheduleHydrateStr}
        `;

        const nextScheduleHydrate = `
            date=${date},
            ${seasonStr}
            limit=${gamesNext},
            gameType=[${gameTypes}],
            ${scheduleHydrateStr}
        `;

        const fullHydrateStr = `
            ${showPrev ? `previousSchedule(${prevScheduleHydrate})` : ''}
            ${showPrev && showNext ? ',' : ''}
            ${showNext ? `nextSchedule(${nextScheduleHydrate})` : ''}
        `.replace(/\s/g, '');

        let { path } = opts;
        path = !useHttps ? path : path.replace('http:', 'https:');

        return `
                ${path}/api/v1/teams/${teamId}
                ?hydrate=${fullHydrateStr}
                &useLatestGames=true
                &language=${lang}
            `.replace(/\s/g, '');

    },

    /**
     *
     * @method getID for mlb or milb
     * @param {String} sport type either mlb or milb
     * @param {String/Number} either sport id or league id
     * @param {String} string
     * @returns {String/Number}
     * @description get sportID or LeagueID for either mlb or milb
     *
     */
    getID(sport, id, type) {
        if (sport === 'milb') {
            if (type === 'sport') return id || 11;
            else return id || '112,117';
        } else {
            if (type === 'sport') return id || 1;
            else return id || '103,104';
        }
    },

    /**
     *
     * @method constructPath for non-MIG modes
     * @param {Object} opts
     * @returns {String}
     * @description path construction based on options
     *
     */
    constructNonMigPath(opts = {}) {

        const { date, useHttps, lang, hydratesBase, hydratesResponsiveAndMIG, sport, sportId, leagueId, teamId, gameTypes } = opts;
        const { hydratesResponsiveOnly, mode, internationalGameIDs, showTeamLeaders, hydrateLeaders, hydratesSeriesStatus, hydratesButtons, hydrateBroadcasts } = opts;
        const igSportID = internationalGameIDs && internationalGameIDs.scoreboard_sportID || '';
        const igLeagueID = internationalGameIDs && internationalGameIDs.scoreboard_leagueID || '';
        const { getID } = ModelHelpers;
        const hasInternationalGameIDs = !!igSportID && !!igLeagueID;
        const sportID = getID(sport, sportId, 'sport');
        const leagueID = getID(sport, leagueId, 'league');
        let fullHydrateObj = hydratesBase;
        let gameTypesParam = gameTypes ? `${'&gameTypes=' + gameTypes}` : '';

        if (mode === 'responsive') {

            if (showTeamLeaders) {

                fullHydrateObj = bamHydrates.merge(hydratesBase, hydratesResponsiveAndMIG, hydratesResponsiveOnly,
                    hydratesButtons, hydratesSeriesStatus, hydrateLeaders);

            } else {

                fullHydrateObj = bamHydrates.merge(hydratesBase, hydratesResponsiveAndMIG, hydratesResponsiveOnly,
                    hydratesButtons, hydratesSeriesStatus);
            }

        } else if (mode === 'minisb') {

            fullHydrateObj = bamHydrates.merge(hydratesBase, hydratesButtons, hydratesSeriesStatus, hydrateBroadcasts);

        }

        const hydrateStr = bamHydrates.stringify(fullHydrateObj);
        const isMlbResponsiveSB = mode === 'responsive' && sport === 'mlb';

        let { path } = opts;
        path = !useHttps ? path : path.replace('http:', 'https:');

        let dataPath = `
                ${path}/api/v1/schedule
                ?sportId=${sportID}${hasInternationalGameIDs ? ',' + igSportID : ''}
                &date=${date}
                ${gameTypesParam}
                &hydrate=${hydrateStr}
                &useLatestGames=false
                ${isMlbResponsiveSB ? '&scheduleTypes=events,games' : ''}
                &language=${lang}
            `.replace(/\s/g, '');

        if (!teamId) {
            dataPath = dataPath + `&leagueId${isMlbResponsiveSB ? 's' : ''}=${leagueID}${hasInternationalGameIDs ? ',' + igLeagueID : ''}`;
        } else if (teamId && ((sport === 'mlb' && mode === 'minisb') || sport === 'milb')) {
            dataPath = dataPath + `&teamId=${teamId}`;
        }

        return dataPath;

    },

    /**
     *
     * @method constructPath
     * @param {Object} opts
     * @returns {String}
     * @description path construction based on options
     *
     */
    constructPath(opts = {}) {

        const { constructMigPath, constructPostSeasonPath, constructNonMigPath } = ModelHelpers;
        const { mode, sport = 'mlb', wiremockPath = '' } = opts;

        if (mode === 'MIG') {
            if (sport === 'milb' && wiremockPath) {
                return `https://wiremock.testeng.mlbinfra.net${wiremockPath}`;
            }
            return constructMigPath(opts);

        } else if (mode === 'postseason') {

            return constructPostSeasonPath(opts);

        } else {

            return constructNonMigPath(opts);

        }

    }

};

export default ModelHelpers;
